import { useMemo, useState, useEffect, useRef } from 'react';
import { addAction, addError } from '@shared/utils/logger';
import { isValidProperty, transformPropertiesToListings } from '@shared/utils/accommodations/AccommodationTiles';
import { isValidCoordinates } from '@shared/core/places';
import type { AccommodationCatalogUnitsOccupancyInput, SearchExtendedAccommodationPropertiesV2Payload } from '@graphql/generated-base';
import { useAccommodationsConsolePropertiesV2Query } from '../AccommodationsConsole.graphql.generated';
import { FetchStrategy } from '@graphql/generated-base';
import type { AccommodationsConsoleState, JoyHotelAccommodationListingDTO } from '@shared/types/Accommodations.types';
import { useExtendedAccommodationSearchMetadata } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { PriceSource } from '@shared/constants/accommodations';
import { isEqual } from 'lodash-es';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';

interface UseAccommodationsConsoleListingsArgs
  extends Readonly<{
    isInitialLoad: boolean;
    state: AccommodationsConsoleState;
    eventHandle: Maybe<string>;
  }> {}

export type ConfirmedSearchPayload = Readonly<{
  location: Readonly<{
    latitude: number;
    longitude: number;
    context: {
      googlePlaceId: string | undefined;
    };
  }> | null;
  searchDates: Readonly<{
    checkin: string;
    checkout: string;
  }>;
  occupancies: AccommodationCatalogUnitsOccupancyInput;
}> | null;

/**
 * Determines if custom search params should be skipped.
 * When true, the query uses only eventHandle (shows event venue first).
 * When false, the query includes custom search params (location, dates, occupancies).
 */
const shouldSkipCustomSearchParams = (isInitialLoad: boolean, hasValidVenueLocation: boolean, hasCustomSearchLocation: boolean): boolean => {
  return isInitialLoad && hasValidVenueLocation && !hasCustomSearchLocation;
};

export const useRegionalHotels = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, isInitialLoad, eventHandle } = args;
  const { currency: currencyCode, loading: loadingTravelCurrencyAllowlist } = useTravelWhiteLabelCurrency();

  const [cachedListings, setCachedListings] = useState<Array<JoyHotelAccommodationListingDTO>>([]);
  const [hasReceivedRegionalData, setHasReceivedRegionalData] = useState(false);
  const [confirmedSearchPayload, setConfirmedSearchPayload] = useState<ConfirmedSearchPayload>(null);

  const { isPreparingSearchMetadata, searchMetadata } = useExtendedAccommodationSearchMetadata();

  const hasValidLocationSearchCoordinates = isValidCoordinates(state.circularRegionSearch);

  const locationPayload = useMemo(() => {
    if (!hasValidLocationSearchCoordinates) {
      return null;
    }
    return {
      latitude: state.circularRegionSearch?.latitude,
      longitude: state.circularRegionSearch?.longitude,
      context: {
        googlePlaceId: state.circularRegionSearch?.googlePlaceId,
      },
    };
  }, [hasValidLocationSearchCoordinates, state.circularRegionSearch?.latitude, state.circularRegionSearch?.longitude, state.circularRegionSearch?.googlePlaceId]);

  const hasValidVenueLocation = !!state.venue.location && isValidCoordinates({ latitude: state.venue.location.lat, longitude: state.venue.location.lng });

  const hasCustomSearchLocation = useMemo(() => {
    if (!hasValidLocationSearchCoordinates || !hasValidVenueLocation) {
      return false;
    }
    return state.venue.location?.lat !== state.circularRegionSearch?.latitude || state.venue.location?.lng !== state.circularRegionSearch?.longitude;
  }, [hasValidLocationSearchCoordinates, hasValidVenueLocation, state.venue.location, state.circularRegionSearch]);

  const skipCustomSearchParams = shouldSkipCustomSearchParams(isInitialLoad, hasValidVenueLocation, hasCustomSearchLocation);

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  // Build payload conditionally - null when we can't build a valid payload
  const rawPayload = useMemo(() => {
    if (!eventHandle || loadingTravelCurrencyAllowlist) {
      return null;
    }

    // When skipping custom search params, only include eventHandle (shows event venue first)
    if (skipCustomSearchParams) {
      return {
        eventHandle,
        customSearchParams: {
          currencyCode,
          useRealtimePricing: false,
          fetchStrategy: FetchStrategy.PREFER_CACHE,
        },
      };
    }

    // When including custom search params, require valid location coordinates
    if (!hasValidLocationSearchCoordinates || !locationPayload || isPreparingSearchMetadata) {
      return null;
    }

    return {
      eventHandle,
      customSearchParams: {
        currencyCode,
        radiusKM: 2,
        location: locationPayload,
        useRealtimePricing: false,
        fetchStrategy: FetchStrategy.PREFER_CACHE,
        stayDates: {
          checkIn: {
            high: state.searchDates.checkin,
            low: state.searchDates.checkin,
          },
          checkOut: {
            high: state.searchDates.checkout,
            low: state.searchDates.checkout,
          },
        },
        occupancies: state.occupancies,
        metadata: searchMetadata,
      },
    };
  }, [
    eventHandle,
    skipCustomSearchParams,
    hasValidLocationSearchCoordinates,
    currencyCode,
    locationPayload,
    isPreparingSearchMetadata,
    state.searchDates.checkin,
    state.searchDates.checkout,
    state.occupancies,
    searchMetadata,
    loadingTravelCurrencyAllowlist,
  ]);

  const payloadRef = useRef(rawPayload);

  if (!isEqual(payloadRef.current, rawPayload)) {
    payloadRef.current = rawPayload;
  }

  const payload = payloadRef.current;

  useEffect(() => {
    setConfirmedSearchPayload(null);
    setCachedListings([]);
    setHasReceivedRegionalData(false);
  }, [payload]);

  const {
    data,
    loading: isFetchingListings,
    error,
  } = useAccommodationsConsolePropertiesV2Query({
    variables: {
      payload: payload!,
    },
    onError: error => {
      addError(error, {
        source: 'Search extended accommodations properties with circularRegionSearch api failed (AccommodationsConsole)',
        context: 'searchExtendedAccommodationPropertiesV2',
        errorMessage: error.message,
        circularRegionSearch: state.circularRegionSearch,
        payload: payload,
      });
    },
    skip: !payload || isBotClient(),
    batchMode: 'off',
    errorPolicy: 'all', // Returns partial data alongside errors instead of failing completely
    ssr: false, // TRAV-2939: Skip during SSR — skeleton UI provides a good loading experience
  });

  useEffect(() => {
    if (data === undefined && !error) {
      return;
    }

    const properties = data?.searchExtendedAccommodationProperties?.properties;
    if (properties) {
      const validProperties = properties.filter(isValidProperty);
      // PREFER_CACHE with useRealtimePricing false, so these prices come from travel_rate_snapshot.
      setCachedListings(transformPropertiesToListings(validProperties, mapHotelRoomPrice, PriceSource.SNAPSHOT));

      if (validProperties.length === 0) {
        addAction('Search extended accommodations properties with circularRegionSearch api with empty results (AccommodationsConsole)', {
          context: 'searchExtendedAccommodationProperties',
          circularRegionSearch: state.circularRegionSearch,
        });
      }
    }

    setHasReceivedRegionalData(true);
  }, [data, error, mapHotelRoomPrice]); // eslint-disable-line react-hooks/exhaustive-deps -- state.circularRegionSearch is only used for logging context

  useEffect(() => {
    const properties = data?.searchExtendedAccommodationProperties?.properties;
    const customParams = payload?.customSearchParams;
    if (properties && customParams?.stayDates && customParams?.occupancies) {
      setConfirmedSearchPayload({
        occupancies: customParams.occupancies,
        location: customParams.location ?? null,
        searchDates: {
          checkin: customParams.stayDates.checkIn.low,
          checkout: customParams.stayDates.checkOut.low,
        },
      });
    }
  }, [data, payload]);

  return {
    cachedListings,
    hasReceivedRegionalData,
    isFetchingListings: isFetchingListings || loadingTravelCurrencyAllowlist,
    error: cachedListings.length === 0 ? error : undefined,
    confirmedSearchPayload,
    payload: payload as SearchExtendedAccommodationPropertiesV2Payload,
  };
};
